/**
 * 幕二 · 备料摘要条 —— 编排后备料台折叠成的一行(2026-08-06 编排页重排)。
 *
 * ═══ 它替代了什么 ═══════════════════════════════════════════════════════════════
 * 编排出分镜之后,备料台那些设置**改了也不生效**(brief 已被服务端快照)。
 * 重排前的做法是让那些面板留在原位、只在作业期间 `disabled` —— 作业一结束又变可点,
 * 用户改了没任何反应:一条**静默死通道**(诊断 P3)。
 * 现在折叠成这一条:说清「这次是按什么编排的」,想改就明说要重编排。
 * 顺带把原来那块独立的「LLM 推断概览」色块并进来 —— 它说的是同一件事(本次实际按什么编排),
 * 没有理由是页面上的第二个 primary 色块。
 *
 * ═══ ★琥珀态的判据不是"点了什么",是"引擎会不会重新读它" ═════════════════════════
 * `stale` 由 `composeDirty.ts` 算,判据钉在 ai 侧 `ComposeRequest` 真消费的字段集上。
 * 改音色 / 字幕 / BGM / 分辨率 → 不报 stale(那些是出片期参数,编排完再调正是它们该待的地方);
 * 改目的 / 风格 / 时长档 / 比例 / 拍法 / 素材 → 报。
 * 圈大了会频繁误报,几次之后用户就学会无视这条 —— 那跟漏报一样是失败。
 *
 * ★D4 拍板:编排后**仍允许改目的**(展开备料台去改),不再像重排前那样直接不给入口。
 *   代价是要把「改了会怎样」当面说清楚,而不是静默失效 —— 这条琥珀态就是那句话。
 */
import { AlertTriangle, RefreshCcw, SlidersHorizontal } from "lucide-react";

import { Button } from "@/shared/ui/button";
import { videoStyleLabelForTone } from "./videoStyleOptions";
import { cn } from "@/shared/lib/utils";
import type { FormState } from "../../model/videoCreateTypes";

interface Props {
  form: FormState;
  /** 备料台是否展开(展开时本条只做标题栏)。 */
  expanded: boolean;
  onToggle: () => void;
  /** 编排输入改过了 → 琥珀 + 重编排入口。 */
  stale: boolean;
  onRecompose: () => void;
  /** 编排/出图进行中:重编排入口禁用(服务端正在写,这时候再发一轮是 dual-write)。 */
  busy?: boolean;
}

export function VideoIntakeSummaryBar({
  form,
  expanded,
  onToggle,
  stale,
  onRecompose,
  busy,
}: Props) {
  const chips = [
    form.briefPurpose,
    form.pickedFormulaName
      ? `拍法「${form.pickedFormulaName}」`
      : form.formulaName
        ? `拍法「${form.formulaName}」`
        : null,
    [form.ratio, form.briefDuration ? `${form.briefDuration}片` : null]
      .filter(Boolean)
      .join(" · ") || null,
    videoStyleLabelForTone(form.briefStyle ?? null),
    (form.entities?.length ?? 0) > 0 ? `素材×${form.entities!.length}` : null,
    form.characterName ? `主角 ${form.characterName}` : null,
  ].filter((v): v is string => !!v && v.trim().length > 0);

  return (
    <div
      data-video-intake-summary
      className={cn(
        "rounded-[calc(var(--radius)*1.15)] border px-4 py-2.5",
        stale ? "border-amber-500/50 bg-amber-500/5" : "border-border/60 bg-card/40"
      )}
    >
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5">
        {stale && <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />}
        {chips.map((c) => (
          <span
            key={c}
            className="rounded-full border border-border/60 bg-background/50 px-2 py-0.5 text-2xs text-foreground"
          >
            {c}
          </span>
        ))}
        {chips.length === 0 && (
          <span className="text-2xs text-muted-foreground">本次按你的文案自由编排</span>
        )}

        <div className="ml-auto flex shrink-0 items-center gap-1.5">
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="h-7 gap-1 text-xs"
            onClick={onToggle}
            aria-expanded={expanded}
          >
            <SlidersHorizontal className="h-3 w-3" />
            {expanded ? "收起设定" : "改设定"}
          </Button>
          {stale && (
            <Button
              type="button"
              size="sm"
              className="h-7 gap-1 text-xs"
              disabled={busy}
              onClick={onRecompose}
            >
              <RefreshCcw className="h-3 w-3" />
              重新编排
            </Button>
          )}
        </div>
      </div>

      {stale && (
        <p className="mt-1.5 text-2xs text-amber-700 dark:text-amber-400">
          这些改动要<span className="font-semibold">重新编排</span>才会进成片;只改配音 / 字幕 / BGM
          / 清晰度不用重编排。
        </p>
      )}

      {/* 「本次实际按什么编排」的补充事实(原 LLM 推断概览):AI 定的主题 / 品牌焦点 / BGM 建议。 */}
      {!stale && (form.theme || form.brandFocus || form.suggestedBgmMood) && (
        <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5 text-2xs text-muted-foreground">
          {form.theme && (
            <span>
              主题:<span className="text-foreground/80">{form.theme}</span>
            </span>
          )}
          {form.brandFocus && <span>品牌焦点:{form.brandFocus}</span>}
          {form.suggestedBgmMood && <span>推荐 BGM:{form.suggestedBgmMood}</span>}
        </div>
      )}
    </div>
  );
}
